import React, { useState, useEffect } from 'react';
import logo from '../TBL Logo White.png';
import './ClassSummary.css';
import { saveClassData, updateClassDataWithEmail, calculateSessionDuration, getModulesCompleted } from '../services/dataCollection';

const ClassSummary = ({ classVersion, onBack, onBackToNavigation }) => {
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [classData, setClassData] = useState({});
  const [documentId, setDocumentId] = useState(null);

  // Load all class data from session storage and save to database
  useEffect(() => {
    try {
      const finalBlendData = JSON.parse(sessionStorage.getItem('finalBlendData') || '{}');
      const wineRatings = JSON.parse(sessionStorage.getItem('wineCharacteristicRatings') || '{}');
      const winePreferences = JSON.parse(sessionStorage.getItem('winePreferences') || '{}');
      const wineVarietals = JSON.parse(sessionStorage.getItem('wineVarietals') || '{}');
      const tryEachWineRatings = JSON.parse(sessionStorage.getItem('wineRatings') || '{}');
      const sessionStartTime = parseInt(sessionStorage.getItem('sessionStartTime') || Date.now());

      console.log('ClassSummary: Loading data:', {
        finalBlendData,
        wineRatings,
        winePreferences,
        wineVarietals,
        tryEachWineRatings
      });

      const classDataForDB = {
        finalBlend: finalBlendData,
        wineRatings,
        winePreferences,
        wineVarietals,
        tryEachWineRatings
      };

      setClassData(classDataForDB);

      // Save class data to Firestore for analytics
      const saveDataToDB = async () => {
        console.log('🔥 ClassSummary Debug: Starting saveDataToDB');
        console.log('🔥 ClassSummary Debug: sessionStartTime:', sessionStartTime);
        
        const sessionDuration = calculateSessionDuration(sessionStartTime);
        const modulesCompleted = getModulesCompleted(classDataForDB);
        
        console.log('🔥 ClassSummary Debug: Calculated sessionDuration:', sessionDuration);
        console.log('🔥 ClassSummary Debug: Calculated modulesCompleted:', modulesCompleted);
        
        const dataToSave = {
          classVersion,
          userEmail: null, // Will be updated when email is submitted
          tryEachWineRatings,
          winePreferences,
          wineCharacteristicRatings: wineRatings,
          finalBlend: finalBlendData,
          wineVarietals,
          sessionDuration,
          modulesCompleted,
          emailSent: false
        };
        
        console.log('🔥 ClassSummary Debug: Data to save:', dataToSave);
        
        const result = await saveClassData(dataToSave);
        
        if (result.success) {
          console.log('🔥 ClassSummary Debug: ✅ Class data saved to database:', result.sessionId);
          console.log('🔥 ClassSummary Debug: Document ID:', result.docId);
          setDocumentId(result.docId); // Store the document ID for later email update
        } else {
          console.error('🔥 ClassSummary Debug: ❌ Failed to save class data:', result.error);
        }
      };

      console.log('🔥 ClassSummary Debug: About to call saveDataToDB');
      saveDataToDB();
      
    } catch (error) {
      console.error('ClassSummary: Error loading data:', error);
      setClassData({
        finalBlend: {},
        wineRatings: {},
        winePreferences: {},
        wineVarietals: {},
        tryEachWineRatings: {}
      });
    }
  }, [classVersion]);

  // Calculate wine scores and rankings
  const calculateWineScores = () => {
    const { wineRatings, wineVarietals } = classData;
    
    // Check if wineRatings exists and has the expected structure
    if (!wineRatings || !wineRatings.acidity || !wineRatings.sweetness || !wineRatings.body) {
      return [];
    }

    const wines = [
      { id: 'wine1', name: 'Wine 1' },
      { id: 'wine2', name: 'Wine 2' },
      { id: 'wine3', name: 'Wine 3' }
    ];

    return wines.map(wine => {
      const acidity = wineRatings.acidity?.[wine.id] || 0;
      const sweetness = wineRatings.sweetness?.[wine.id] || 0;
      const body = wineRatings.body?.[wine.id] || 0;
      const totalScore = acidity + sweetness + body;
      
      return {
        ...wine,
        acidity,
        sweetness,
        body,
        totalScore,
        varietal: wineVarietals?.[wine.id] || 'Not Selected'
      };
    }).sort((a, b) => b.totalScore - a.totalScore);
  };

  const wineRankings = calculateWineScores();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      try {
        // Prepare email data
        const emailData = {
          to_email: email,
          blend_name: classData.finalBlend?.blendName || 'Unnamed Blend',
          wine_rankings: wineRankings,
          wine_preferences: classData.winePreferences,
          final_blend: classData.finalBlend,
          wine_varietals: classData.wineVarietals,
          class_version: classVersion,
          timestamp: new Date().toLocaleDateString()
        };

        // Send email via Vercel email server
        const response = await fetch('https://classapp-cpwjg566c-michael-kellers-projects-5993a122.vercel.app/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(emailData)
        });

        if (response.ok) {
          setEmailSubmitted(true);
          console.log('Email sent successfully');
          
          // Update the existing database record with the user's email
          if (documentId) {
            const updateResult = await updateClassDataWithEmail(documentId, email);
            if (updateResult.success) {
              console.log('🔥 ClassSummary Debug: ✅ Email added to existing record');
            } else {
              console.error('🔥 ClassSummary Debug: ❌ Failed to update record with email:', updateResult.error);
            }
          } else {
            console.error('🔥 ClassSummary Debug: ❌ No document ID available for email update');
          }
        } else {
          // Fallback: show success message even if backend isn't set up yet
          setEmailSubmitted(true);
          console.log('Email data prepared:', emailData);
        }
      } catch (error) {
        // Fallback: show success message even if backend isn't set up yet
        setEmailSubmitted(true);
        console.log('Email data prepared (fallback):', {
          to_email: email,
          blend_name: classData.finalBlend?.blendName || 'Unnamed Blend',
          wine_rankings: wineRankings,
          wine_preferences: classData.winePreferences,
          final_blend: classData.finalBlend,
          wine_varietals: classData.wineVarietals,
          class_version: classVersion,
          timestamp: new Date().toLocaleDateString()
        });
        
        // Still update the database record with email even if email service fails
        if (documentId) {
          const updateResult = await updateClassDataWithEmail(documentId, email);
          if (updateResult.success) {
            console.log('🔥 ClassSummary Debug: ✅ Email added to existing record (fallback)');
          } else {
            console.error('🔥 ClassSummary Debug: ❌ Failed to update record with email (fallback):', updateResult.error);
          }
        }
      }
    }
  };

  const handleRestart = () => {
    // Clear all session storage and restart
    sessionStorage.clear();
    window.location.reload();
  };

  return (
    <div className={`class-summary ${classVersion}-theme`}>
      <div className="summary-header">
        <img src={logo} className="summary-logo" alt="TBL Logo" />
        <h1>Class Complete!</h1>
        <p className="summary-description">
          Congratulations on completing the wine blending class! Here's a summary of your experience.
        </p>
      </div>

      <div className="summary-container">
        {/* Final Blend Summary */}
        <div className="summary-section">
          <div className="blend-summary-card">
            <div className="blend-name-display">
              <h3>{classData.finalBlend?.blendName || 'Unnamed Blend'}</h3>
            </div>
            <div className="blend-percentages">
              {classData.finalBlend?.percentages ? Object.entries(classData.finalBlend.percentages).map(([wineId, percentage]) => {
                const varietal = classData.wineVarietals?.[wineId] || 'Not Selected';
                const mlAmount = Math.round((percentage / 100) * 375); // 375mL bottle
                return (
                  <div key={wineId} className="percentage-item">
                    <span className="wine-label">{wineId.replace('wine', 'Wine ')}</span>
                    <span className="varietal-label">({varietal})</span>
                    <span className="percentage-value">{percentage}%</span>
                    <span className="ml-amount">{mlAmount}mL</span>
                  </div>
                );
              }) : (
                <p>No blend data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Wine Rankings */}
        <div className="summary-section">
          <h2>Your Wine Preferences (Ranked)</h2>
          <div className="wine-rankings">
            {wineRankings.length > 0 ? wineRankings.map((wine, index) => (
              <div key={wine.id} className="wine-ranking-card">
                <div className="ranking-number">#{index + 1}</div>
                <div className="wine-info">
                  <h3>{wine.name}</h3>
                  <p className="varietal">{wine.varietal}</p>
                  <div className="score-breakdown">
                    <span>Total Score: <strong>{wine.totalScore.toFixed(1)}/30</strong></span>
                    <div className="characteristic-scores">
                      <span>Acidity: {wine.acidity.toFixed(1)}</span>
                      <span>Sweetness: {wine.sweetness.toFixed(1)}</span>
                      <span>Body: {wine.body.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <p>No wine ratings available</p>
            )}
          </div>
        </div>

        {/* Wine Preferences */}
        <div className="summary-section">
          <h2>Your Wine Preferences</h2>
          <div className="preferences-summary">
            {classData.winePreferences && Object.keys(classData.winePreferences).length > 0 ? Object.entries(classData.winePreferences).map(([characteristic, level]) => (
              <div key={characteristic} className="preference-item">
                <span className="characteristic-name">{characteristic}</span>
                <span className="preference-level">{level}</span>
              </div>
            )) : (
              <p>No preference data available</p>
            )}
          </div>
        </div>

        {/* Email Submission */}
        <div className="summary-section">
          <h2>Email Your Class Summary</h2>
          <div className="email-section">
            {!emailSubmitted ? (
              <form onSubmit={handleEmailSubmit} className="email-form">
                <p className="email-description">
                  Would you like to receive a detailed summary of your class experience via email?
                </p>
                <div className="email-input-container">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address..."
                    className="email-input"
                    required
                  />
                  <button type="submit" className="submit-email-button">
                    Send Summary
                  </button>
                </div>
              </form>
            ) : (
              <div className="email-success">
                <p>✅ Thank you! Your class summary will be sent to {email}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="summary-actions">
        <button className="back-to-edit-button" onClick={onBackToNavigation}>
          Back to Edit
        </button>
        <button className="restart-button" onClick={handleRestart}>
          Start New Class
        </button>
      </div>

      <div className="footer">
        <div className="footer-separator"></div>
        <img src={logo} className="footer-logo" alt="TBL Logo" />
      </div>
    </div>
  );
};

export default ClassSummary;
